import axios from 'axios'
import * as Sentry from '@sentry/gatsby'
import {
  SPAN_STATUS_ERROR,
  isPageUnloading,
  syncSentryUser,
} from './sentryContext'

const PUBLIC_API_URL = process.env.MIDDLE_END_URL_PUSH_MONEY

// Ruta sin query y con ids genéricos, para agrupar issues y transacciones.
const routeOf = url =>
  url
    .slice(PUBLIC_API_URL.length)
    .split('?')[0]
    .replace(/\/\d+(?=\/|$)/g, '/:id') || '/'

// Varios endpoints responden 2xx con el error real en body.statusCode
// (por ejemplo POST /email-request: 201 con statusCode 500).
// En el adapter axios todavía no parseó el JSON (transformResponse corre
// después), así que data puede llegar como string.
const bodyStatusOf = response => {
  let data = response?.data
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data)
    } catch {
      return undefined
    }
  }
  const status = data?.statusCode
  return typeof status === 'number' ? status : undefined
}

const reportPublicApiError = (requestInfo, error, status) => {
  Sentry.addBreadcrumb({
    category: 'public-api.error',
    level: 'error',
    message: requestInfo.label,
    data: { status },
  })

  // Los 4xx son validaciones o sesión vencida: quedan en el span y en el
  // breadcrumb. Abren issue los 5xx y las fallas de red.
  if ((status && status < 500) || (!status && isPageUnloading())) {
    return
  }
  Sentry.withScope(scope => {
    scope.setTag('public_api.route', requestInfo.route)
    scope.setTag('http.method', requestInfo.method)
    if (status) {
      scope.setTag('http.status_code', status)
    } else {
      scope.setTag('public_api.network_error', true)
    }
    scope.setContext('public_api', { ...requestInfo, status })
    scope.setFingerprint(['public-api', requestInfo.label, String(status)])
    Sentry.captureException(error)
  })
}

// Cada request a push-money-public-api abre su propia transacción dentro de
// la traza de la página. El adapter XHR de axios abre y envía el request
// sincrónicamente al invocarlo, así que su span http.client queda como hijo y
// propaga sentry-trace/baggage a public-api.
export const installSentryAxiosAdapter = () => {
  if (!PUBLIC_API_URL || typeof window === 'undefined') {
    return
  }
  const defaultAdapter = axios.getAdapter(axios.defaults.adapter)

  axios.defaults.adapter = config => {
    const url = axios.getUri(config)
    if (!url.startsWith(PUBLIC_API_URL)) {
      return defaultAdapter(config)
    }
    const method = (config.method || 'get').toUpperCase()
    const route = routeOf(url)
    const requestInfo = { method, route, label: `${method} ${route}` }

    syncSentryUser()

    return Sentry.startSpanManual(
      {
        name: requestInfo.label,
        op: 'public_api.client',
        forceTransaction: true,
        attributes: {
          'http.request.method': method,
          'http.route': route,
        },
      },
      span =>
        defaultAdapter(config).then(
          response => {
            const bodyStatus = bodyStatusOf(response)
            Sentry.setHttpStatus(
              span,
              bodyStatus >= 400 ? bodyStatus : response.status,
            )
            if (bodyStatus >= 400) {
              span.setAttribute('public_api.body_status_code', bodyStatus)
              Sentry.withActiveSpan(span, () =>
                reportPublicApiError(
                  requestInfo,
                  new Error(`${requestInfo.label} respondió ${bodyStatus}`),
                  bodyStatus,
                ),
              )
            }
            span.end()
            return response
          },
          error => {
            const status = error?.response?.status
            if (status) {
              Sentry.setHttpStatus(span, status)
            } else {
              span.setStatus({
                code: SPAN_STATUS_ERROR,
                message: 'unavailable',
              })
            }
            Sentry.withActiveSpan(span, () =>
              reportPublicApiError(requestInfo, error, status),
            )
            span.end()
            throw error
          },
        ),
    )
  }
}
